import React, { useEffect, useRef, useState } from 'react';
import { Button, CONTROL, DIVIDER, FormField, INK, MUTED, Overlay, RED, SearchField, SelectField, TEAL, Translate, hintStyle, labelStyle } from '../../components/purchasing';
import { getGroupProduct } from '../../services/items.services';
import { getListAttributes } from '../../services/attributes.service';
import { COUNT_TYPE, createCount } from '../../services/count.service';
import { showApiError } from '../../services/apiError';
import { showToast } from '../../services/toast.service';
import { useDebounceEffect } from '../../hook/useDebounceEffect';

interface Option {
  id: string;
  name: string;
}

interface NewCountDialogProps {
  open: boolean;
  phone: boolean;
  t: Translate;
  language: any;
  sites: Option[];
  reasons: Option[];
  defaultSiteId: string;
  onClose: () => void;
  onCreated: (id: string) => void;
}

const CHOICE_LIMIT = 100;

export const COUNT_KINDS = [
  { type: COUNT_TYPE.All, label: 'All products', hint: 'Every countable product of the site.' },
  { type: COUNT_TYPE.Group, label: 'Product groups', hint: 'The countable products of the groups you choose.' },
  { type: COUNT_TYPE.Attribute, label: 'Attributes', hint: 'The countable products with the attributes you choose.' },
  { type: COUNT_TYPE.Individual, label: 'Chosen products', hint: 'Starts empty; add the products you count.' },
];

/** Searchable checkboxes; what is chosen stays chosen while the search changes. */
const ChoiceList: React.FC<{
  id: string;
  phone: boolean;
  t: Translate;
  label: string;
  options: Option[];
  loading: boolean;
  search: string;
  onSearch: (value: string) => void;
  chosen: Map<string, string>;
  onToggle: (option: Option) => void;
  invalid: boolean;
}> = ({ id, phone, t, label, options, loading, search, onSearch, chosen, onToggle, invalid }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
    <SearchField phone={phone} label={t('Search {list}', { list: label })} placeholder={t('Search')} value={search} onChange={onSearch} />
    <div
      role="group"
      aria-labelledby={`${id}-label`}
      aria-invalid={invalid || undefined}
      aria-describedby={invalid ? `${id}-error` : undefined}
      style={{ maxHeight: phone ? '260px' : '220px', overflowY: 'auto', border: `1px solid ${invalid ? RED : CONTROL}`, borderRadius: '10px' }}
    >
      {options.length === 0 ? (
        <p style={{ padding: '12px', fontSize: '14px', lineHeight: '20px', color: MUTED }}>{loading ? t('Loading…') : t('Nothing matches "{search}"', { search: search.trim() })}</p>
      ) : (
        options.map((option, index) => (
          <label
            key={option.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              minHeight: phone ? '48px' : '40px',
              padding: '0 12px',
              borderTop: index === 0 ? 'none' : `1px solid ${DIVIDER}`,
              fontSize: phone ? '15px' : '14px',
              lineHeight: '20px',
              color: INK,
              cursor: 'pointer',
            }}
          >
            <input type="checkbox" checked={chosen.has(option.id)} onChange={() => onToggle(option)} style={{ width: '16px', height: '16px', accentColor: TEAL, flex: 'none' }} />
            <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{option.name}</span>
          </label>
        ))
      )}
    </div>
    {chosen.size > 0 && (
      <p style={hintStyle}>
        {chosen.size === 1 ? t('1 chosen: {names}', { names: [...chosen.values()].join(', ') }) : t('{count} chosen: {names}', { count: chosen.size, names: [...chosen.values()].join(', ') })}
      </p>
    )}
  </div>
);

/** Site, what to count and why; the count opens next with its products listed. */
const NewCountDialog: React.FC<NewCountDialogProps> = ({ open, phone, t, language, sites, reasons, defaultSiteId, onClose, onCreated }) => {
  const [siteId, setSiteId] = useState('');
  const [type, setType] = useState<number>(COUNT_TYPE.All);
  const [reasonId, setReasonId] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState('');
  const [term, setTerm] = useState('');
  const [options, setOptions] = useState<Option[]>([]);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [chosen, setChosen] = useState<Map<string, string>>(() => new Map());
  const seq = useRef(0);

  const choosing = type === COUNT_TYPE.Group || type === COUNT_TYPE.Attribute;

  useEffect(() => {
    if (!open) return;
    setSiteId(defaultSiteId && sites.some((site) => site.id === defaultSiteId) ? defaultSiteId : sites.length === 1 ? sites[0].id : '');
    setType(COUNT_TYPE.All);
    setReasonId(reasons.length === 1 ? reasons[0].id : '');
    setErrors({});
    setSearch('');
    setTerm('');
    setChosen(new Map());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useDebounceEffect(() => setTerm(search.trim()), [search], 300);

  useEffect(() => {
    if (!open || !choosing) return;
    const mine = ++seq.current;
    setOptionsLoading(true);
    const companyId = localStorage.getItem('companyId') || '';
    const request =
      type === COUNT_TYPE.Group
        ? getGroupProduct(companyId, 1, CHOICE_LIMIT, term).then((response: any) =>
            (response?.data?.productGroupWithCompany ?? []).map((row: any) => ({ id: String(row.id), name: String(row.groupName ?? '') })),
          )
        : getListAttributes(companyId, 1, CHOICE_LIMIT, term).then((response: any) =>
            (response?.data?.data?.attributes ?? []).map((row: any) => ({ id: String(row.id), name: String(row.displayName || row.attributeName || '') })),
          );
    request
      .then((rows: Option[]) => {
        if (mine === seq.current) setOptions(rows);
      })
      .catch((error: any) => {
        if (mine === seq.current) showApiError(error, { language });
      })
      .finally(() => {
        if (mine === seq.current) setOptionsLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, type, term]);

  const pickType = (next: number) => {
    setType(next);
    setSearch('');
    setTerm('');
    setOptions([]);
    setChosen(new Map());
    setErrors((prev) => ({ ...prev, choices: '' }));
  };

  const clearError = (key: string) => setErrors((prev) => (prev[key] ? { ...prev, [key]: '' } : prev));
  const toggle = (option: Option) => {
    clearError('choices');
    setChosen((prev) => {
      const next = new Map(prev);
      if (next.has(option.id)) next.delete(option.id);
      else next.set(option.id, option.name);
      return next;
    });
  };

  const create = async () => {
    const nextErrors: Record<string, string> = {};
    if (!siteId) nextErrors.site = t('Choose the site you count');
    if (!reasonId) nextErrors.reason = t('Choose a reason');
    if (type === COUNT_TYPE.Group && chosen.size === 0) nextErrors.choices = t('Choose at least one product group');
    if (type === COUNT_TYPE.Attribute && chosen.size === 0) nextErrors.choices = t('Choose at least one attribute');
    setErrors(nextErrors);
    if (Object.values(nextErrors).some(Boolean)) return;
    setBusy(true);
    try {
      const response: any = await createCount({
        locationId: siteId,
        type,
        reasonId,
        ...(type === COUNT_TYPE.Group ? { productGroupIds: [...chosen.keys()] } : {}),
        ...(type === COUNT_TYPE.Attribute ? { attributeIds: [...chosen.keys()] } : {}),
      });
      const created = response?.data?.data;
      const skipped = Number(created?.withoutUnit) || 0;
      if (skipped > 0) {
        showToast({
          message: skipped === 1 ? t('1 product has no base unit and was left off the count.') : t('{count} products have no base unit and were left off the count.', { count: skipped }),
          type: 'warning',
          duration: 7000,
        });
      }
      if (created?.id) onCreated(created.id);
      else onClose();
    } catch (error: any) {
      showApiError(error, { language });
    } finally {
      setBusy(false);
    }
  };

  const footer = (
    <div style={{ display: 'flex', flexDirection: phone ? 'column-reverse' : 'row', justifyContent: 'flex-end', gap: phone ? '8px' : '12px' }}>
      <Button phone={phone} variant={phone ? 'text' : 'secondary'} onClick={onClose}>
        {t('Cancel')}
      </Button>
      <Button phone={phone} variant="primary" fullWidth={phone} disabled={busy} aria-busy={busy || undefined} onClick={create}>
        {busy ? t('Starting count…') : t('Start count')}
      </Button>
    </div>
  );

  const kind = COUNT_KINDS.find((entry) => entry.type === type);
  const listLabel = type === COUNT_TYPE.Group ? t('Product groups') : t('Attributes');

  return (
    <Overlay open={open} phone={phone} title={t('New count')} titleId="pu-new-count-title" closeLabel={t('Close')} onClose={onClose} footer={footer} width={520}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', paddingTop: '4px' }}>
        <FormField id="pu-new-count-site" label={t('Site')} error={errors.site}>
          <SelectField
            id="pu-new-count-site"
            phone={phone}
            invalid={!!errors.site}
            aria-invalid={!!errors.site || undefined}
            aria-describedby={errors.site ? 'pu-new-count-site-error' : undefined}
            value={siteId}
            onChange={(event) => {
              setSiteId(event.target.value);
              clearError('site');
            }}
          >
            <option value="">{t('Choose a site')}</option>
            {sites.map((site) => (
              <option key={site.id} value={site.id}>
                {site.name}
              </option>
            ))}
          </SelectField>
        </FormField>
        <FormField id="pu-new-count-kind" label={t('What to count')} hint={kind && <p style={hintStyle}>{t(kind.hint)}</p>}>
          <SelectField id="pu-new-count-kind" phone={phone} value={String(type)} onChange={(event) => pickType(Number(event.target.value))}>
            {COUNT_KINDS.map((entry) => (
              <option key={entry.type} value={entry.type}>
                {t(entry.label)}
              </option>
            ))}
          </SelectField>
        </FormField>
        {choosing && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <span id="pu-new-count-choices-label" style={labelStyle}>
              {listLabel}
            </span>
            <ChoiceList
              id="pu-new-count-choices"
              phone={phone}
              t={t}
              label={listLabel}
              options={options}
              loading={optionsLoading}
              search={search}
              onSearch={setSearch}
              chosen={chosen}
              onToggle={toggle}
              invalid={!!errors.choices}
            />
            {errors.choices && (
              <p id="pu-new-count-choices-error" style={{ margin: 0, fontSize: '12px', lineHeight: '16px', color: RED }}>
                {errors.choices}
              </p>
            )}
          </div>
        )}
        <FormField id="pu-new-count-reason" label={t('Reason')} error={errors.reason}>
          <SelectField
            id="pu-new-count-reason"
            phone={phone}
            invalid={!!errors.reason}
            aria-invalid={!!errors.reason || undefined}
            aria-describedby={errors.reason ? 'pu-new-count-reason-error' : undefined}
            value={reasonId}
            onChange={(event) => {
              setReasonId(event.target.value);
              clearError('reason');
            }}
          >
            <option value="">{t('Choose a reason')}</option>
            {reasons.map((reason) => (
              <option key={reason.id} value={reason.id}>
                {reason.name}
              </option>
            ))}
          </SelectField>
        </FormField>
      </div>
    </Overlay>
  );
};

export default NewCountDialog;
